import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import * as React from 'react';
import { ListItem } from './ListItem.js';
import { list, appender } from './styles.js';
import { ThemeProvider } from 'styled-components';
import { useTheme } from '../../theming/useTheme.js';
import { lookup } from '../../helpers/array.js';

function getActiveIndex(items) {
  return function (active) {
    return items.findIndex(function (item) {
      return item.value === active;
    });
  };
}
/**
 * Handle item that is currently hovered / selected with keyboard.
 */


function useHighlightActive(opened, items, selected, onSelect, onClose, disableFocusTransfer, onKeyboardNavigateTopOut) {
  // for multiselect do not set active item by default
  var activeValue = Array.isArray(selected) ? undefined : selected;
  var activeRef = React.useRef(null);

  var _React$useState = React.useState(activeValue),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      active = _React$useState2[0],
      setActive = _React$useState2[1];

  var _React$useState3 = React.useState(items.find(function (item) {
    return item.value === activeValue;
  })),
      _React$useState4 = _slicedToArray(_React$useState3, 2),
      activeItem = _React$useState4[0],
      setActiveItem = _React$useState4[1];

  var _React$useState5 = React.useState(false),
      _React$useState6 = _slicedToArray(_React$useState5, 2),
      keyboardNavigation = _React$useState6[0],
      setKeyboardNavigation = _React$useState6[1];

  var lastKeyboardNavigation = React.useRef(0);
  React.useEffect(function () {
    setActiveItem(items.find(function (item) {
      return item.value === active;
    }));
  }, [active, items]); // Reset active item if list is toggled

  React.useEffect(function () {
    setActive(activeValue);
  }, [opened]); // Reset active item if list is toggled or items change.

  React.useEffect(function () {
    if (getActiveIndex(items)(active) !== -1) {
      // `active` item still exists in list, use it
      setActive(active);
    } else if (items[0]) {
      if (disableFocusTransfer) return; // Otherwise use first item if it exists

      setActive(items[0].value);
    } else {
      setActive(undefined);
    }
  }, [selected, items, disableFocusTransfer]); // Set active item on mouse hover

  var handleMouseMove = React.useCallback(function (value) {
    // If there's no change, skip expensive state update (this callback might
    // run many tens of times per second)
    if (value === active && keyboardNavigation === false) return; // Ignore event if there has been a keyboard navigation event in the last 500ms

    if (new Date().getTime() - lastKeyboardNavigation.current < 500) return;
    setKeyboardNavigation(false);
    setActive(value);
  }, [setActive, active]); // Set active item on keyboard list navigation

  React.useEffect(function () {
    var handleKeyDown = function handleKeyDown(e) {
      if (e.key === 'ArrowDown') {
        var _activeRef$current;

        e.preventDefault();
        var index = getActiveIndex(items)(active);
        var newIndex = Math.min(index + 1, items.length - 1);
        var item = lookup(items, newIndex);
        setKeyboardNavigation(true);
        lastKeyboardNavigation.current = new Date().getTime();
        if (item) setActive(item.value);
        activeRef === null || activeRef === void 0 ? void 0 : (_activeRef$current = activeRef.current) === null || _activeRef$current === void 0 ? void 0 : _activeRef$current.focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();

        var _index = getActiveIndex(items)(active);

        if (_index - 1 < 0) {
          onKeyboardNavigateTopOut();
          setActive(undefined);
        } else {
          var _activeRef$current2;

          var _item = lookup(items, _index - 1); // istanbul ignore else


          if (_item) setActive(_item.value);
          setKeyboardNavigation(true);
          lastKeyboardNavigation.current = new Date().getTime();
          activeRef === null || activeRef === void 0 ? void 0 : (_activeRef$current2 = activeRef.current) === null || _activeRef$current2 === void 0 ? void 0 : _activeRef$current2.focus();
        }
      } else if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
        if (!active) return;
        e.preventDefault();
        onSelect(active);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    if (opened) {
      document.addEventListener('keydown', handleKeyDown);
    } else {
      document.removeEventListener('keydown', handleKeyDown);
    }

    return function () {
      return document.removeEventListener('keydown', handleKeyDown);
    };
  }, [opened, active, items, onSelect, onKeyboardNavigateTopOut]);
  return {
    active: active,
    setActive: setActive,
    handleMouseMove: handleMouseMove,
    keyboardNavigation: keyboardNavigation,
    setKeyboardNavigation: setKeyboardNavigation,
    activeRef: activeRef,
    activeItem: activeItem
  };
}

var useScrollToActive = function useScrollToActive(showItems, activeIndex, keyboardNavigation, activeRef) {
  var listRef = React.useRef(null); // Scroll to active element on keyboard navigation

  React.useLayoutEffect(function () {
    if (!keyboardNavigation) return;
    var halfOfShowItems = Math.round((showItems - 1) / 2); // istanbul ignore next: doesn't work in jsdom?

    if (activeIndex > -1 && listRef.current) {
      listRef.current.scrollTop = (activeIndex - halfOfShowItems) * 64;
    }
  }, [showItems, activeIndex, listRef]); // Scroll to active element when list opens

  React.useEffect(function () {
    var list = listRef.current;
    var active = activeRef === null || activeRef === void 0 ? void 0 : activeRef.current; // istanbul ignore next: typescript guard

    if (!list || !active) return;
    var y = active.offsetTop; // istanbul ignore next: doesn't work in jsdom?

    if (list.scrollTo) {
      list.scrollTo({
        top: y
      });
    }
  }, []);
  return {
    listRef: listRef
  };
};
/**
 * Jumps to entry starting with letters typed by user.
 * Typed letters time out after 1s since last input.
 *
 * @param opened Whether list is opened or not
 * @param items List items
 * @param setActive Callback for setting active list item
 * @param setKeyboardNavigation Callback for activating keyboard navigation mode
 */


function useJumpToLetter(opened, items, setActive, setKeyboardNavigation) {
  var enteredKeys = React.useRef('');
  var lastKey = React.useRef(0); // Set active item on keyboard list navigation

  React.useEffect(function () {
    var handleKeyDown = function handleKeyDown(e) {
      // If pressed key was a special key (e.g. ArrowLeft), ignore event
      if (e.key.length !== 1) {
        // Reset key timer
        lastKey.current = 0;
        return;
      }

      setKeyboardNavigation(true);
      var letter = e.key[0];
      var t = Date.now(); // User has 1s between keystrokes before we reset search string

      if (t - lastKey.current < 1000) {
        // Append letter to search
        enteredKeys.current += letter;
      } else {
        // Replace search string with letter
        enteredKeys.current = letter;
      }

      lastKey.current = t;
      var search = enteredKeys.current.toLowerCase();
      e.preventDefault();
      var match = items.find(function (item) {
        return item.name.toLowerCase().startsWith(search);
      });
      if (match) setActive(match.value);
    };

    if (opened) {
      document.addEventListener('keydown', handleKeyDown);
    } else {
      document.removeEventListener('keydown', handleKeyDown);
    }

    return function () {
      return document.removeEventListener('keydown', handleKeyDown);
    };
  }, [opened, items, setActive]);
}

function ListItems(props) {
  var _props$enableJumpToLe = props.enableJumpToLetter,
      enableJumpToLetter = _props$enableJumpToLe === void 0 ? false : _props$enableJumpToLe,
      _props$showItems = props.showItems,
      showItems = _props$showItems === void 0 ? 5 : _props$showItems,
      disabled = props.disabled,
      highlightValue = props.highlightValue,
      id = props.id,
      items = props.items,
      name = props.name,
      onSelect = props.onSelect;
  props.onClick;

  var onClose = props.onClose,
      _props$opened = props.opened,
      opened = _props$opened === void 0 ? false : _props$opened,
      selected = props.selected,
      _props$offset = props.offset,
      offset = _props$offset === void 0 ? 0 : _props$offset,
      _props$disableFocusTr = props.disableFocusTransfer,
      disableFocusTransfer = _props$disableFocusTr === void 0 ? false : _props$disableFocusTr,
      color = props.color,
      onKeyboardNavigateTopOut = props.onKeyboardNavigateTopOut,
      _props$disableBorders = props.disableBorders,
      disableBorders = _props$disableBorders === void 0 ? false : _props$disableBorders,
      boxShadow = props.boxShadow,
      highlightSelected = props.highlightSelected,
      ariaMultiselectable = props['aria-multiselectable'],
      rest = _objectWithoutProperties(props, ["enableJumpToLetter", "showItems", "disabled", "highlightValue", "id", "items", "name", "onSelect", "onClick", "onClose", "opened", "selected", "offset", "disableFocusTransfer", "color", "onKeyboardNavigateTopOut", "disableBorders", "boxShadow", "highlightSelected", "aria-multiselectable"]);

  var _useHighlightActive = useHighlightActive(opened, items, selected, onSelect, onClose, disableFocusTransfer, onKeyboardNavigateTopOut),
      active = _useHighlightActive.active,
      handleMouseMove = _useHighlightActive.handleMouseMove,
      keyboardNavigation = _useHighlightActive.keyboardNavigation,
      setActive = _useHighlightActive.setActive,
      setKeyboardNavigation = _useHighlightActive.setKeyboardNavigation,
      activeRef = _useHighlightActive.activeRef,
      activeItem = _useHighlightActive.activeItem;

  var theme = useTheme();

  var _React$useState7 = React.useState(false),
      _React$useState8 = _slicedToArray(_React$useState7, 2),
      openAndTransitioned = _React$useState8[0],
      setOpenAndTransiotioned = _React$useState8[1];

  React.useEffect(function () {
    if (!opened) {
      setOpenAndTransiotioned(false);
    }
  }, [opened]);
  if (enableJumpToLetter) useJumpToLetter(opened, items, setActive, setKeyboardNavigation);

  var _useScrollToActive = useScrollToActive(showItems, getActiveIndex(items)(active), keyboardNavigation, activeRef),
      listRef = _useScrollToActive.listRef;

  var maxItems = items.length <= showItems ? items.length : showItems;
  var maxHeight = opened ? maxItems * 64 + 2 : 0;
  var duration = maxItems * 10 + 150;
  var hidden = !opened || items.length <= 0;
  return (
    /*#__PURE__*/
    React.createElement(ThemeProvider, {
      theme: theme
    },
    /*#__PURE__*/
    React.createElement("div", {
      style: {
        position: 'absolute',
        width: '100%'
      }
    },
    /*#__PURE__*/
    React.createElement(list, _extends({
      id: id,
      "data-test": "elemental-ListItems",
      ref: listRef,
      opened: items.length === 0 ? false : opened,
      scrollbar: openAndTransitioned,
      onTransitionEnd: function onTransitionEnd(e) {
        // istanbul ignore else
        if (e.propertyName === 'max-height' && opened) {
          setOpenAndTransiotioned(true);
        }
      } // TODO: any type
      ,
      maxHeight: maxHeight,
      transitionDuration: duration,
      role: "listbox",
      opacity: opened ? 1 : 0,
      "aria-hidden": !opened,
      offset: offset,
      color: color || theme.listItems.web.color,
      disableBorders: disableBorders,
      boxShadow: boxShadow || theme.listItems.web.boxShadow,
      "aria-activedescendant": activeItem ? "".concat(id, "-").concat(activeItem === null || activeItem === void 0 ? void 0 : activeItem.value, "-active") : undefined,
      "aria-multiselectable": ariaMultiselectable
    }, rest), items.map(function (item, idx) {
      var delay = opened ? duration / maxItems * (idx < showItems ? idx : showItems) : 0; // we want this undefined if not

      var isSelected = selected ? Array.isArray(selected) ? selected === null || selected === void 0 ? void 0 : selected.includes(item.value) : selected === item.value : undefined;
      var isActive = active === item.value;
      return (
        /*#__PURE__*/
        React.createElement(ListItem, {
          id: isActive ? "".concat(id, "-").concat(item.value, "-active") : "".concat(id, "-").concat(item.value),
          disabled: disabled,
          highlightValue: highlightValue,
          highlightSelected: highlightSelected,
          key: idx,
          name: name,
          onSelect: onSelect,
          onMouseMove: handleMouseMove,
          active: isActive,
          selected: isSelected,
          delay: delay,
          value: item.value,
          activeRef: isActive && opened ? activeRef : undefined,
          tabIndex: isActive && opened ? 0 : -1,
          "aria-posinset": idx + 1,
          "aria-setsize": items.length,
          "aria-multiselectable": ariaMultiselectable,
          color: color
        }, item.name)
      );
    })), !disableBorders &&
    /*#__PURE__*/
    React.createElement(appender, {
      offset: offset,
      color: color || theme.listItems.web.color,
      borderRadius: props.borderRadius,
      hidden: hidden
    })))
  );
}

export { ListItems };